// Segmented - a row of mutually-exclusive pill buttons; the standard in-card
// toggle / range control (metering window, overage policy, time range).
// Styled to match the admin nav's active-tab treatment. One implementation,
// replacing the near-identical copies that had drifted into a couple of pages.

import type { ReactNode } from 'react';
import { cn } from '@/lib/classnames';

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  fullWidth,
  ariaLabel,
  className,
}: {
  // `title` carries the reason an option is out of reach. A disabled option stays
  // on screen so the constraint is visible rather than mysterious.
  options: ReadonlyArray<{ value: T; label: ReactNode; disabled?: boolean; title?: string }>;
  value: T;
  onChange: (v: T) => void;
  // Stretch buttons to fill the row (in-card selectors); omit for a compact
  // inline control (toolbars / range pickers).
  fullWidth?: boolean;
  ariaLabel?: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex rounded-lg border border-ink-800 bg-ink-950/40 p-0.5',
        fullWidth && 'w-full',
        className,
      )}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={o.disabled}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={cn(
              // min-h-11 is the 44px touch target, dropped back to the compact height once
              // a fine pointer is present. Coarse-pointer devices are the ones that need
              // the size, and a mouse does not, so the density is kept where it is earned.
              'ring-focus rounded-md px-3 py-1.5 text-data font-medium transition-colors',
              'min-h-[44px] [@media(pointer:fine)]:min-h-0',
              fullWidth && 'flex-1',
              active
                ? 'bg-accent-500/15 text-accent-200 shadow-[inset_0_0_0_1px_rgb(var(--accent-400)/0.25)]'
                : 'text-ink-400 hover:text-ink-200',
              o.disabled && 'opacity-40 cursor-not-allowed hover:text-ink-400',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
